/**
 * @module cercaliagl/Marker
 */
import bboxPolygon from '@turf/bbox-polygon';
import turfPoint from 'turf-point';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import LonLat from './LonLat';
import BaseFeature from './BaseFeature';
import { createDefault } from './Icon';
import { getAnchorStringEquivalent } from './util.js';

/**
 * @typedef {Object} MarkerOptions
 * @property {string} [id] Id
 * @property {import('./Icon.js').default} [icon] Icon
 * @property {maplibregl.LngLat} position Position
 * @property {string} encodedGooglePosition Position encoded on google polyline format.
 * @property {boolean} [draggable] Draggable. Default `false`.
 * @property {function(this:Marker): void} [onClick] Function to execute on marker click.
 * @property {function(this:Marker): void} [onDoubleClick] Function to execute on marker double click.
 * @property {function(this:Marker): void} [onMouseOut] Function to execute on Mouse Out.
 * @property {function(this:Marker): void} [onMouseOver] Function to execute on Mouse Over.
 * @property {function(this:Marker): void} [onRightClick] Function to execute on marker right click.
 * @property {function(this:Marker): void} [onDragEnd] Function to execute when ends marker dragging.
 * @property {function(this:Marker): void} [onDragMove] Function to execute during marker dragging.
 * @property {function(this:Marker): void} [onDragStart] Function to execute on marker dragging start.
 * @property {function(this:Marker): void} [onDragStart] Function to be executed when marker visibility changes.
 * @property {function(this:Marker): void} [onDrop] Function to obtain markers on dragged marker extent when drag ends.
 * @property {import('./SimpleLabel').default} [simpleLabel] Marker Simple Label object.
 * @property {boolean} [visible] Marker visible or not. Default `true`.
 * @property {import('./Label').default} [label] Label
 * @property {number} [zIndex] Z-Index. Default 0.
 * @property {Object.<string, string>} [extraProperties] Extra properties to put inside geojson. Default {}.
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const map = new cercaliagl.Marker({ ... });
 * ```
 *
 * Create a marker component. Marker will draw a feature on map with an icon {@link module:cercaliagl/Icon~Icon Icon}.
 * Marker optionally can have a  {@link module:cercaliagl/Label~Label Label}, {@link module:cercaliagl/SimpleLabel~SimpleLabel SimpleLabel}
 * and {@link module:cercaliagl/Popup~Popup Popup}
 * <br/>
 * There are many events which marker can handle.<br/>
 * These events can defined in constructor or in runtime modifing attribute as `onClick`, `onMouseOver`, etc.<br/>
 *
 * Example usage:
 *
 * ```js
 * const map = new cercaliagl.Map({
 *   target: 'map'
 * });
 *
 * const marker = new cercaliagl.Marker({
 *   position: new cercaliagl.LonLat(1.241, 41.431),
 *   label: new cercaliagl.Label({text: 'Label text'}),
 *   simpleLabel: new cercaliagl.SimpleLabel({text: 'Simple label text'}),
 *   popup: new cercaliagl.Popup({
 *     title: 'Title popup',
 *     content: '<b>Content</b> popup',
 *   }),
 *   draggable: true,
 *   onClick: () => map.setCenter(marker.getPosition()),
 * });
 * map.addMarker(marker);
 * ```
 *
 * @extends {BaseFeature}
 * @api
 */
class Marker extends BaseFeature {
  /**
   * @constructor
   * @param {MarkerOptions} options Options
   * @api
   */
  constructor(options = {}) {
    super({
      id: options.id,
      onClick: options.onClick,
      onDoubleClick: options.onDoubleClick,
      onMouseOut: options.onMouseOut,
      onMouseOver: options.onMouseOver,
      onRightClick: options.onRightClick,
    });

    /**
     * @private
     * @type {import('./Icon').default}
     */
    this.icon_ = options.icon ? options.icon : createDefault();
    /**
     * @private
     * @type {import('./LonLat').default}
     */
    this.position_ = options.position;
    /**
     * @private
     * @type {import('./LonLat').default}
     */
    this.encodedGooglePosition_ = options.encodedGooglePosition;
    /**
     * @private
     * @type {import('./LonLat').default}
     */
    this.encodedPosition_ = options.encodedPosition;

    /**
     * @private
     * @type {number}
     */
    this.draggable_ = options.draggable !== undefined ? options.draggable : false;

    /**
     * @type {function(this:Marker) : void|null}
     */
    this.onVisibilityChange = options.onVisibilityChange ? options.onVisibilityChange : null;

    /**
     * @type {function(this:Marker) : void|null}
     */
    this.onDragStart = options.onDragStart ? options.onDragStart : null;

    /**
     * @type {function(this:Marker) : void|null}
     */
    this.onDragMove = options.onDragMove ? options.onDragMove : null;

    /**
     * @type {function(this:Marker) : void|null}
     */
    this.onDragEnd = options.onDragEnd ? options.onDragEnd : null;

    /**
     * @type {function(this:Marker) : void|null}
     */
    this.onDrop = options.onDrop ? options.onDrop : null;

    /**
     * @private
     * @type {import("./Map").default}
     */
    this.map_ = null;

    /**
     * @private
     * @type {import("./Popup").default|null}
     */
    this.popup_ = options.popup ? options.popup : null;

    /**
     * @private
     * @type {import('./Label').default|null}
     */
    this.label_ = options.label ? options.label : null;

    /**
     * @private
     * @type {import('./SimpleLabel').defaulhis.simp|null}
     */
    this.simpleLabel_ = options.simpleLabel ? options.simpleLabel : null;

    /**
     * @private
     * @type {boolean}
     */
    this.visible_ = options.visible !== undefined ? options.visible : true;
    /**
     * @private
     * @type {number}
     */
    this.zIndex_ = options.zIndex !== undefined ? options.zIndex : 0;
    /**
     * @private
     * @type {Object.<string, string>}
     */
    this.extraProperties_ = options.extraProperties ? options.extraProperties : {};

    if (!options.position) {
      this.initializePosition_(options);
    }
    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    this.handleChange_ = this.handleChange_.bind(this);
    this.icon_.on('change', this.handleChange_);
    if (this.simpleLabel_) {
      this.simpleLabel_.setBaseFeature(this);
      this.simpleLabel_.on('change', this.handleChange_);
    }

    this.updateGeoJSON_();
  }

  /**
   * @private
  */
  initializePosition_(options) {
    let position;
    if (options.encodedPosition) {
      position = this.encodedDataToPointPosition_(options.encodedPosition, true);
    } else if (options.encodedGooglePosition) {
      position = this.encodedDataToPointPosition_(options.encodedGooglePosition, false);
    }
    this.position_ = position;
  }

  /**
   * @private
   */
  handleChange_() {
    this.updateGeoJSON_();
    if (this.layer_) {
      this.layer_.updateFeatureGeoJSON(this);
      this.layer_.refreshGeoJSON();
    }
  }

  /**
   * @private
   * @return {Object} GeoJSON
   */
  updateGeoJSON_() {
    let simpleLabelProps = {};
    if (this.simpleLabel_) {
      simpleLabelProps = {
        textFont: ['Roboto Medium Italic'],
        textSize: this.simpleLabel_.getFontSize(),
        textPadding: 20,
        textAnchor: this.simpleLabel_.getAnchor(),
        textJustify: this.simpleLabel_.getJustify(),
        textOffset: this.simpleLabel_.getOffset(),
        textField: this.simpleLabel_.getText(),
        textVisible: this.simpleLabel_.isVisible(),
        textOpacity: this.simpleLabel_.getTextOpacity(),
        textColor: this.simpleLabel_.getTextColor(),
        textRotation: this.simpleLabel_.getTextRotation(),
        strokeColor: this.simpleLabel_.getStrokeColor(),
        strokeWidth: this.simpleLabel_.getStrokeWidth(),
      };
    }

    this.geojson_ = {
      type: 'Feature',
      properties: {
        id: `${this.getId()}`,
        draggable: this.draggable_,
        icon: this.icon_.getSrc() ? this.icon_.getSrc() : this.icon_.getLoadedIconImage(),
        'icon-size': this.icon_.getScale(),
        iconOpacity: this.icon_.getOpacity(),
        anchor: this.icon_.getAnchor(),
        offset: this.icon_.getOffset(),
        rotation: this.icon_.getRotation(),
        visible: this.visible_,
        zIndex: this.zIndex_,
        textSize: 0,
        textOffset: [0, 0],
        textAnchor: 'top',
        textField: ' ',
        textRotation: 0,
        textJustify: 'center',
        ...simpleLabelProps,
        ...this.extraProperties_,
      },
      geometry: {
        type: 'Point',
        coordinates: this.position_.toArray(),
      },
    };
  }

  /**
   * Get marker icon
   * @return {import('./Icon.js').default} Icon
   * @api
   */
  getIcon() {
    return this.icon_;
  }

  /**
   * Set marker icon.
   * @param {import('./Icon.js').default} icon Icon
   * @api
   */
  setIcon(icon) {
    this.icon_ = icon;
    this.icon_.on('change', this.handleChange_);
    this.handleChange_();
  }

  /**
   * @return {LonLat} Position
   */
  getPosition() {
    return this.position_;
  }

  /**
   * @return {maplibregl.Popup} Popup
   * @api
   */
  getPopup() {
    return this.popup_;
  }

  /**
   * @param {maplibregl.Popup} popup Popup
   * @api
   */
  setPopup(popup) {
    this.popup_ = popup;
  }

  /**
   * @return {boolean} visible Visible
   * @api
  */
  getVisible() {
    return this.visible_;
  }

  /**
   * Hide or show marker.
   * @param {boolean} visible Visible
   * @api
   */
  setVisible(visible) {
    if (visible !== this.visible_ && this.onVisibilityChange) {
      this.onVisibilityChange(this, visible);
    }
    this.visible_ = visible;
    if (this.simpleLabel_) {
      this.simpleLabel_.setVisible(visible);
    }
    if (this.label_) {
      if (visible) {
        this.label_.show();
      } else {
        this.label_.hide();
      }
    }
    this.updateGeoJSON_();
    this.handleChange_();
  }

  /**
   * @override
   * @param {import('./Map').default} map Map
   */
  setMap(map) {
    let mbPopup;
    if (map) {
      if (this.map_ === null) {
        this.map_ = map;
        if (this.label_) {
          mbPopup = this.label_.getMapboxPopup();
          mbPopup.addTo(this.map_.getMapboxglMap());
          this.label_.setMarker(this);
        }
        if (this.popup_ && this.popup_.isVisible()) {
          this.openPopup();
        }
      }
    } else {
      if (this.map_) {
        this.closePopup();
        if (this.label_) {
          mbPopup = this.label_.getMapboxPopup();
          mbPopup.remove();
        }
      }
      this.map_ = null;
    }
  }

  /**
   * @param {import("./Popup").default} popup PPopup
   * @api
   */
  addPopup(popup) {
    this.popup_ = popup;
    this.popup_.setPosition(this.position_);
    if (this.map_ !== null) {
      this.addPopupInner_();
    }
  }

  /**
   * @private
   */
  addPopupInner_() {
    const mbPopup = this.popup_.getMapboxPopup();
    mbPopup.setLngLat(this.position_.toMapboxLngLat());
    mbPopup.addTo(this.map_.getMapboxglMap());
  }

  /**
   * Open popup
   * @api
   */
  openPopup() {
    if (this.popup_) {
      this.addPopupInner_();
    }
  }

  /**
   * Close popup
   * @api
   */
  closePopup() {
    if (this.popup_) {
      const mbPopup = this.popup_.getMapboxPopup();
      mbPopup.remove();
    }
  }

  /**
   * Assign/change marker label text
   * @param {import('./Label').default} label Label content
   * @api
   */
  setLabel(label) {
    if (this.label_) {
      this.removeLabel();
    }

    if (this.map_) {
      this.label_ = label;
      this.label_.setPosition(this.position_);
      this.label_.getMapboxPopup().addTo(this.map_.getMapboxglMap());
    }
  }

  /**
   * Show marker label
   * @api
   */
  showLabel() {
    if (this.label_) {
      this.label_.show();
    }
  }

  /**
   * Hide marker label
   * @api
   */
  hideLabel() {
    if (this.label_) {
      this.label_.hide();
    }
  }

  /**
   * Get label
   * @return {import('./Label').default} label Label assigned to th marker.
   * @api
   */
  getLabel() {
    return this.label_;
  }

  /**
   * Remove label
   * @api
   */
  removeLabel() {
    if (this.label_) {
      if (this.map_ !== null) {
        this.label_.getMapboxPopup().remove();
      }
      this.label_ = null;
    }
  }

  /**
   * Set position
   * @param {LonLat|import('maplibre-gl').LngLat} position Position
   * @param {boolean} avoidRefresh Avoid refresh
   * @api
   */
  setPosition(position, avoidRefresh = false) {
    this.position_ = position instanceof LonLat ? position : LonLat.fromMapboxgl(position);
    this.geojson_.geometry.coordinates = this.position_.toArray();
    if (this.popup_) {
      this.popup_.setPosition(this.position_);
    }

    if (this.label_) {
      this.label_.setPosition(this.position_);
    }
    if (!avoidRefresh) {
      this.layer_.updateGeoJSON();
    }
  }

  /**
   * Set marker as draggable
   * @param {boolean} draggable Draggable
   * @api
   */
  setDraggable(draggable) {
    this.draggable_ = draggable;
    this.handleChange_();
  }

  /**
   * @return {boolean} draggable Draggable
   * @api
   */
  isDraggable() {
    return this.draggable_;
  }

  /**
   * Sets the function to be called when the marker has started to be dragged.
   * @api
   * @param {function} onDragStart - The function to be called when the marker has started to be dragged.
   */
  setOnDragStart(onDragStart) {
    this.onDragStart = onDragStart;
  }

  /**
   * Sets the function to be called when the marker has changed his visibility.
   * @api
   * @param {function} onVisibilityChange - The function to be called when the marker has changed his visibility.
   */
  setOnVisibilityChange(onVisibilityChange) {
    this.onVisibilityChange = onVisibilityChange;
  }

  /**
   * Sets the function to be called when the marker is dragged and released.
   * @api
   * @param {function} onDragEnd - The function to be called when the marker is dragged and released.
   */
  setOnDragEnd(onDragEnd) {
    this.onDragEnd = onDragEnd;
  }

  /**
   * Sets the function to be called when the marker is moved.
   * @api
   * @param {function} onDragMove - The function to be called when the marker is moved
   */
  setOnDragMove(onDragMove) {
    this.onDragMove = onDragMove;
  }

  /**
   * Get simple label
   * @return {import('./SimpleLabel').default} Simple label
   */
  getSimpleLabel() {
    return this.simpleLabel_;
  }

  /**
   * Set simpel label. If `null` simple label will be removed
   * @param {import('./SimpleLabel').default|null} simpleLabel Simple Label
   * @api
   */
  setSimpleLabel(simpleLabel) {
    if (simpleLabel) {
      this.simpleLabel_ = simpleLabel;
      this.simpleLabel_.setBaseFeature(this);
      this.simpleLabel_.on('change', this.handleChange_);
    } else {
      this.simpleLabel_.off('change', this.handleChange_);
      this.simpleLabel_ = null;
    }
    this.handleChange_();
  }

  /**
   * Get zIndex
   * @return {number} zIndex zIndex
   * @api
   */
  getZIndex() {
    return this.zIndex_;
  }

  /**
   * Set zIndex and update it.
   * @api
   * @param {number} zIndex zIndex
  */
  setZIndex(zIndex) {
    this.zIndex_ = zIndex;
    this.handleChange_();
  }

  /**
 * @private
 */
  onDropFunction_() {
    const pixel = this.map_.coordToPixel(this.getPosition());
    const icon = this.getIcon();
    const iconWidth = icon.getWidth();
    const iconHeight = icon.getHeight();
    const iconScale = icon.getScale();
    const iconSize = [iconWidth * iconScale, iconHeight * iconScale];
    const iconAnchor = this.getIcon().getAnchor();
    const anchorArray = typeof iconAnchor === 'string' ? getAnchorStringEquivalent(iconAnchor, iconWidth, iconHeight, iconScale) : iconAnchor;

    const centerPixel = [
      pixel[0] + iconSize[0] / 2 - anchorArray[0],
      pixel[1] + iconSize[1] / 2 - anchorArray[1],
    ];

    // Calculate the corners of the polygon based on the center of the icon
    const pixelNE = [centerPixel[0] + iconSize[0] / 2, centerPixel[1] - iconSize[1] / 2];
    const pixelSW = [centerPixel[0] - iconSize[0] / 2, centerPixel[1] + iconSize[1] / 2];

    // Buscamos las features (tipo marker)
    const coordNE = this.map_.pixelToCoord(pixelNE);
    const coordSW = this.map_.pixelToCoord(pixelSW);

    const turfBboxPolygon = bboxPolygon([coordSW.getLon(), coordSW.getLat(), coordNE.getLon(), coordNE.getLat()]);

    const layerGeojson = this.layer_.getGeoJSON();

    const markersOnExtent = layerGeojson.features
      .filter((feature) => feature.properties.id !== this.id_ && feature.properties.visible && booleanPointInPolygon(turfPoint(feature.geometry.coordinates), turfBboxPolygon))
      .map((feature) => this.map_.getMarkerById(feature.properties.id, this.layer_));

    this.onDrop(markersOnExtent);
  }

  /**
   * Get extra properties
   * @return {Object.<string, string>} Extra properties
   * @api
   */
  getExtraProperties() {
    return this.extraProperties_;
  }

  /**
     * Set extra properties
     * @param {Object.<string, string>} properties Extra properties
     * @api
     */
  setExtraProperties(properties) {
    this.extraProperties_ = properties;
    this.handleChange_();
  }
}

export default Marker;
